import React, { useState, useRef } from "react";
import {
  Box,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SelectAllIcon from "@mui/icons-material/SelectAll";

const EditorBox = ({ value, onChange }) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("Text copied!");
  const textareaRef = useRef(null);

  // 🔹 Select All content in textarea
  const handleSelectAll = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    ta.select();
    // Also try to copy after selecting
    try {
      navigator.clipboard.writeText(ta.value).then(() => {
        setToastMsg("All text selected & copied!");
        setShowToast(true);
      });
    } catch {
      setToastMsg("All text selected!");
      setShowToast(true);
    }
  };

  // 🔹 Copy handler — copies full content
  const handleCopy = () => {
    const text = value || "";
    navigator.clipboard.writeText(text).then(() => {
      setToastMsg("Text copied!");
      setShowToast(true);
    }).catch(() => {
      // fallback
      const ta = textareaRef.current;
      if (ta) {
        ta.select();
        document.execCommand("copy");
      }
      setToastMsg("Text copied!");
      setShowToast(true);
    });
  };

  // 🔹 Text formatting handlers — works on selected text, falls back to full
  const handleFormat = (formatType) => {
    const ta = textareaRef.current;
    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);
    const before = value.substring(0, start);
    const after = value.substring(end);

    let replacement = selected;

    switch (formatType) {
      case "bold":
        replacement = selected ? `**${selected}**` : `**bold text**`;
        break;
      case "italic":
        replacement = selected ? `*${selected}*` : `*italic text*`;
        break;
      case "underline":
        replacement = selected ? `__${selected}__` : `__underline text__`;
        break;
      case "list":
        if (selected) {
          replacement = selected
            .split("\n")
            .map((line) => (line.trim() ? `• ${line}` : ""))
            .join("\n");
        } else {
          replacement = `• `;
        }
        break;
      default:
        break;
    }

    const newValue = before + replacement + after;
    onChange(newValue);

    // Restore cursor position after state update
    requestAnimationFrame(() => {
      if (ta) {
        const newEnd = before.length + replacement.length;
        ta.focus();
        ta.setSelectionRange(before.length, newEnd);
      }
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Outer Box */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: "#fafafa",
          overflow: "hidden",
          width: "100%",
          boxSizing: "border-box",
          minHeight: 0,
        }}
      >
        {/* Textarea */}
        <Box
          sx={{
            flex: 1,
            px: 2,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            "& textarea": {
              flex: 1,
              width: "100%",
              minHeight: "140px",
              height: "100%",
              border: "none",
              outline: "none",
              resize: "none",
              backgroundColor: "#fafafa",
              fontSize: "14px",
              fontFamily: "inherit",
              lineHeight: 1.6,
              color: "#333",
              boxSizing: "border-box",
            },
          }}
        >
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Write something..."
          />
        </Box>

        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f5f1ff",
            padding: "6px 12px",
            borderTop: "1px solid #e0e0e0",
            flexShrink: 0,
          }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Bold selection">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleFormat("bold")}
              >
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic selection">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleFormat("italic")}
              >
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Underline selection">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleFormat("underline")}
              >
                <FormatUnderlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Bullet list">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleFormat("list")}
              >
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Select all & copy">
              <IconButton
                size="small"
                onClick={handleSelectAll}
                color="primary"
                sx={{
                  backgroundColor: "rgba(0,71,171,0.08)",
                  borderRadius: "6px",
                  "&:hover": { backgroundColor: "rgba(0,71,171,0.18)" },
                }}
              >
                <SelectAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy text">
              <IconButton size="small" onClick={handleCopy} color="primary">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={showToast}
        autoHideDuration={2000}
        onClose={() => setShowToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowToast(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditorBox;
